import { useAdAccountCRUDActions } from '@mm/state/actions/AdAccountCRUDActions';
import {
  getPromoterOrTrigger,
  useAutomationsCRUDActions,
} from '@mm/state/actions/automationActions';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { useCtmAdCRUDActions } from '@mm/state/actions/CtmAdCRUDActions';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { CampaignType } from '@mm/state/model/CampaignsModel';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { maxBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useSyncedMessengerAdFactory } from '../../../Campaigns/use-synced-messenger-factory';
import { useDispatch, useSelector } from 'react-redux';
import { TemplateParams, TriggerType } from './TriggersDefinitions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { Dispatch } from 'redux';
import { StoreState } from '@mm/state/store-interfaces';
import { dispatchCampaignPostUpdates } from '@mm/state/actions/campaign-post-updates';

interface ICreateHandler {
  ({
    campaign,
    params,
  }: {
    campaign: CampaignType;
    params?: TemplateParams;
  }): Promise<ReturnType<typeof getPromoterOrTrigger>>;
}

export const useInputTriggerCRUD = () => {
  const botId = useSelector(activeChatbotIdSelector);
  const fbPageId = useSelector(activeFacebookPageIdSelector);

  const campaignCRUDActions = useCampaignCRUDActions();
  const pageCRUDActions = usePageCRUDActions();
  const promoterCRUDActions = usePromoterCRUDActions();
  const widgetCRUDActions = useWidgetCRUDActions();
  const automationCRUDActions = useAutomationsCRUDActions();
  const viewActions = useViewActions();
  const ctmAdCRUDActions = useCtmAdCRUDActions();
  const adAccountCRUDActions = useAdAccountCRUDActions();
  const syncedMessengerAdFactory = useSyncedMessengerAdFactory({
    botId,
  });

  const createPageForCampaign = useCallback(
    async (campaign: CampaignType, prefix: string, extraParams?: any) => {
      const pageGroupId =
        campaign.schedulersAttributes?.[0]?.destinationPageGroupId;
      if (!campaign || !pageGroupId) {
        return;
      }

      const result = await pageCRUDActions.create({
        botId,
        name: `${prefix} ${Date.now()}`,
        pageGroupId,
        ...extraParams,
      });
      return result.page;
    },
    [botId, pageCRUDActions],
  );

  const attachAudienceToCampaign = useCallback(
    async ({
      campaign,
      audienceDefinition,
    }: {
      campaign: CampaignType;
      audienceDefinition:
        | {
            promoted_promoter_id: number;
          }
        | { viewed_page_id: number }
        | { audienceId: number }
        | { id: number; destroy: true };
    }) => {
      const {
        acceptableTime,
        active,
        id,
        name,
        platforms,
        purpose,
        scheduleType,
        schedulersAttributes,
      } = campaign;
      await campaignCRUDActions.updateCampaign(
        {
          acceptableTime,
          active,
          id,
          name,
          platforms,
          purpose,
          scheduleType,
          schedulersAttributes,

          campaignAudiencesAttributes: [
            ...(campaign.campaignAudiencesAttributes || [])
              .filter(a => a.name === 'All contacts')
              .map(a => ({
                ...a,
                destroy: true,
              })),
            { ...audienceDefinition, kind: 'included' },
          ],
        } as CampaignType,
        botId,
      );
    },
    [campaignCRUDActions, botId],
  );

  const addViewedPageTriggerToAutomation = useCallback(
    ({ campaign, pageId }: { campaign: CampaignType; pageId: number }) => {
      attachAudienceToCampaign({
        campaign,
        audienceDefinition: { viewed_page_id: pageId },
      });
    },
    [attachAudienceToCampaign],
  );

  const addPromotedPromoterTriggerToAutomation = useCallback(
    ({
      campaign,
      promoterId,
    }: {
      campaign: CampaignType;
      promoterId: number;
    }) => {
      attachAudienceToCampaign({
        campaign,
        audienceDefinition: { promoted_promoter_id: promoterId },
      });
    },
    [attachAudienceToCampaign],
  );

  const dispatch: Dispatch<StoreState> = useDispatch();

  const createInputTrigger = useCallback<ICreateHandler>(
    async ({ campaign, params }) => {
      if (!params) {
        return;
      }

      const updatedCampaign = await automationCRUDActions.createInputTrigger({
        campaignId: campaign.id,
        templateParams: params,
        botId,
      });

      const newestCampaignAudienceAttribute = maxBy(
        updatedCampaign.campaignAudiencesAttributes,
        a => a.audienceId,
      );

      if (updatedCampaign?.id) {
        dispatchCampaignPostUpdates(dispatch, updatedCampaign);
      }

      return getPromoterOrTrigger(newestCampaignAudienceAttribute);
    },
    [automationCRUDActions, botId],
  );

  const removeTriggerFromAutomation = useCallback(
    ({
      campaign,
      audienceId,
    }: {
      campaign: CampaignType;
      audienceId: number;
    }) => {
      return attachAudienceToCampaign({
        campaign,
        audienceDefinition: { audienceId, destroy: true },
      });
    },
    [attachAudienceToCampaign],
  );

  const uploadContactListTrigger = useCallback(
    async ({
      campaign,
      audienceId,
      addEmailPlatform,
    }: {
      campaign: CampaignType;
      audienceId: number;
      addEmailPlatform?: boolean;
    }) => {
      let platforms = campaign.platforms || [];
      if (addEmailPlatform && !platforms.includes('email')) {
        platforms = [...platforms, 'email'];
      }
      await attachAudienceToCampaign({
        campaign: { ...campaign, platforms },
        audienceDefinition: { audienceId },
      });
    },
    [attachAudienceToCampaign],
  );

  const removeContactListTrigger = useCallback(
    async ({
      campaign,
      audienceId,
    }: {
      campaign: CampaignType;
      audienceId: number;
    }) => {
      return attachAudienceToCampaign({
        campaign,
        audienceDefinition: { audienceId, destroy: true },
      });
    },
    [attachAudienceToCampaign],
  );

  const createChatBlasterTrigger = useCallback(
    async ({ campaign }: { campaign: CampaignType }) => {
      const page = await createPageForCampaign(campaign, 'Blast');
      if (!page) {
        return;
      }

      await addViewedPageTriggerToAutomation({ campaign, pageId: page.id });
      viewActions.showSlideoutMenu('smsBlasterEditor', {
        campaignId: campaign.id,
        pageId: page.id,
      });
      return page;
    },
    [addViewedPageTriggerToAutomation, createPageForCampaign, viewActions],
  );

  const createGetemailsPromoter = useCallback(
    async ({
      campaign,
      pageId,
    }: {
      campaign: CampaignType;
      pageId?: number;
    }) => {
      let pageIdToUse = pageId;

      if (!pageIdToUse) {
        const page = await createPageForCampaign(
          campaign,
          'Getemails Trigger',
          {
            hidden: true,
            needsReview: false,
          },
        );
        if (!page) {
          return;
        }
        pageIdToUse = page.id;
      }

      const promoter = await promoterCRUDActions.create({
        active: false,
        botId,
        description: '',
        name: 'Web Visitor Email Detector',
        kind: 'getemails',
        type: 'getemails',
        pageId: pageIdToUse,
      });
      if (!promoter.id) {
        return;
      }
      let platforms = campaign.platforms || [];
      if (!platforms.includes('email')) {
        platforms = [...platforms, 'email'];
      }

      await attachAudienceToCampaign({
        campaign: { ...campaign, platforms },
        audienceDefinition: { promoted_promoter_id: promoter.id },
      });

      return promoter;
    },
    [],
  );

  const createCtmAd = useCallback(
    async ({ campaign }: { campaign: CampaignType }) => {
      const adAccounts = await adAccountCRUDActions.retrieveAll(
        fbPageId,
        false,
        '',
      );
      const ad = syncedMessengerAdFactory();
      const ctmAd = await ctmAdCRUDActions.create(botId, {
        ...ad,
        adAccountId: adAccounts[0].id,
        adType: 'synced_messenger_ad',
      });

      const params = {
        inputTriggers: [{ kind: 'ctm_ads' }],
        ctmAdId: ctmAd.id,
      };

      await pageCRUDActions.update({
        botId,
        id: ctmAd.ad.pageId,
        needsReview: false,
      });

      await automationCRUDActions.createInputTrigger({
        campaignId: campaign.id,
        templateParams: params,
        botId,
      });

      return ctmAd;
    },
    [],
  );

  const createInputTriggerAndOpenSlideout = useCallback(
    async (
      campaign: CampaignType,
      triggerType: TriggerType,
      params?: TemplateParams,
    ) => {
      if (!campaign) {
        return;
      }

      const pageId = params?.inputTriggers?.find(v => v.pageId)?.pageId;

      try {
        switch (triggerType) {
          case 'sms_keywords':
          case 'instagram_keywords':
          case 'facebook_keywords': {
            const newTrigger = await createInputTrigger({
              campaign,
              params,
            });
            if (newTrigger) {
              const SLIDEOUT_TYPE = {
                sms_keywords: 'smsKeywordEditor',
                instagram_keywords: 'qaEditor',
                facebook_keywords: 'qaEditor',
              } as const;
              viewActions.showSlideoutMenu(SLIDEOUT_TYPE[triggerType], {
                triggerId: newTrigger.id,
              });
            }

            break;
          }
          case 'instagram_welcomer':
          case 'welcome_page': {
            const welcomer = await createInputTrigger({
              campaign,
              params,
            });
            if (welcomer) {
              viewActions.showSlideoutMenu(
                triggerType === 'instagram_welcomer'
                  ? 'igWelcomerEditor'
                  : 'fbWelcomerEditor',
              );
            }

            break;
          }
          case 'import_contacts':
            viewActions.showSlideoutMenu('uploadContactListEditor', {
              campaignId: campaign.id,
            });
            break;
          case 'web_visitor_email_detector': {
            const promoter = await createGetemailsPromoter({
              campaign,
              pageId,
            });
            if (promoter) {
              viewActions.showSlideoutMenu('getemailsEditor', {
                promoterId: promoter.id,
              });
            }
            break;
          }
          case 'ctm_ads': {
            const ctmAd = await createCtmAd({ campaign });
            if (ctmAd) {
              viewActions.showSlideoutMenu('ctmAdsEditor', {
                ad: ctmAd,
                skipAdAccountsRetrieve: true,
                campaignId: campaign.id,
              });
            }
            break;
          }
          case 'instagram_comment_guard':
          case 'instagram_story_mention':
          case 'instagram_story_reaction':
          case 'fb_comment_guard':
          case 'instagram_live_comment_guard':
          case 'link':
          case 'instagram_link':
          case 'customer_chat': {
            const newTrigger = await createInputTrigger({
              campaign,
              params,
            });
            if (newTrigger) {
              if ('pageId' in newTrigger && newTrigger?.pageId) {
                const pageFetches = [
                  pageCRUDActions.retrieveById(newTrigger.pageId),
                ];
                if (triggerType === 'customer_chat') {
                  pageFetches.push(
                    widgetCRUDActions.retrieveAll(newTrigger.pageId),
                  );
                }

                await Promise.all(pageFetches);
              }
              const SLIDEOUT_TYPE = {
                instagram_comment_guard: 'igCommentGuardEditor',
                instagram_story_mention: 'igStoryMentionEditor',
                instagram_story_reaction: 'igStoryReactionEditor',
                fb_comment_guard: 'fbCommentGuardEditor',
                instagram_live_comment_guard: 'igLiveEditor',
                link: 'shareLinksEditor',
                instagram_link: 'shareLinksEditor',
                customer_chat: 'customerChatSlideoutEditor',
              } as const;
              const platform =
                triggerType === 'link'
                  ? 'facebook'
                  : triggerType === 'instagram_link'
                  ? 'instagram'
                  : undefined;

              viewActions.showSlideoutMenu(SLIDEOUT_TYPE[triggerType], {
                promoterId: newTrigger.id,
                platform,
              });
            }

            break;
          }
          default:
            break;
        }
      } catch (err) {}
    },
    [createInputTrigger, pageCRUDActions, viewActions],
  );

  return useMemo(
    () => ({
      addPromotedPromoterTriggerToAutomation,
      addViewedPageTriggerToAutomation,
      createInputTrigger,
      removeTriggerFromAutomation,
      createChatBlasterTrigger,
      uploadContactListTrigger,
      removeContactListTrigger,
      createInputTriggerAndOpenSlideout,
    }),
    [
      addPromotedPromoterTriggerToAutomation,
      addViewedPageTriggerToAutomation,
      createInputTrigger,
      removeTriggerFromAutomation,
      createChatBlasterTrigger,
      uploadContactListTrigger,
      removeContactListTrigger,
      createInputTriggerAndOpenSlideout,
    ],
  );
};
